<template><div><BlogPost :author="$frontmatter.author" :posted="parse($frontmatter.posted, 'y-M-d', new Date())" :heading="$frontmatter.heading">
<p class="d-docsite--paragraph"><strong>TL;DR:</strong> We're rolling out an important update to Dialtone <RouteLink to="/design/typography/#styles" class="d-docsite--link d-link">Typography Styles</RouteLink> based on an adjustment to our Design Tokens naming convention.</p>
<p class="d-docsite--paragraph"><strong>👋 Hey there,</strong> we're excited to share some updates regarding our Typography Tokens. This change brings clarity and consistency to our design system, making your workflow smoother than ever. Let's jump right in:</p>
<p class="d-docsite--paragraph"><img src="/assets/images/new_types_cover_1.gif" alt="Updates on Typography Tokens" class="d-docsite--image d-wmx100p"></p>
<h2 id="the-change" tabindex="-1" class="d-docsite--header-2">The Change <a class="header-anchor d-link d-docsite--link d-link" href="#the-change" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We've revamped our Typography Tokens naming convention to ensure consistency across Figma styles, CSS Utilities, and CSS Variables. This update simplifies how we reference and utilize our design elements, improving efficiency for both designers and engineers.</p>
<h3 id="why-the-update" tabindex="-1" class="d-docsite--header-3">Why the Update? <a class="header-anchor d-link d-docsite--link d-link" href="#why-the-update" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Our previous naming convention, while functional, lacked uniformity and clarity. In particular, its previous naming convention caused unnecessary complexity and lacked a predictable naming pattern. By refining the naming structure, we've making it easier for you to locate and utilize the styles you need in HTML, Vue, CSS, Figma deliverables, or otherwise.</p>
<h4 class="d-docsite--header-4">Key Advantages:</h4>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph"><strong>Simplified CSS usage,</strong> now all types are named with this structure: <code v-pre>d-{category}--{size}-{strength*}-{density*}</code>, e.g. <code v-pre>d-body--sm-compact</code></p>
</li>
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph"><strong>Aligned Across Platforms,</strong> Figma styles more strictly match the naming convention of CSS Utilities and CSS Variables, making integration smoother and collaboration better.</p>
</li>
</ul>
<p class="d-docsite--paragraph">It's important to note that this update doesn’t alter the appearance of any styles but rather enhances how we name and utilize them.</p>
<h2 id="how-to-update" tabindex="-1" class="d-docsite--header-2">How to Update <a class="header-anchor d-link d-docsite--link d-link" href="#how-to-update" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Backwards-compatible support for the previous CSS Utilities will remain for the near-term. However, we encourage you to begin updating your projects to the new convention for a seamless experience.</p>
<h3 id="using-token-css-variable" tabindex="-1" class="d-docsite--header-3">Using Token / CSS Variable <a class="header-anchor d-link d-docsite--link d-link" href="#using-token-css-variable" aria-hidden="true">#</a></h3>
<div class="language-less" data-highlighter="prismjs" data-ext="less"><pre v-pre><code class="language-less"><span class="line"><span class="token function">var</span><span class="token punctuation">(</span><span class="token operator">-</span><span class="token operator">-</span>dt<span class="token operator">-</span>typography<span class="token operator">-</span>body<span class="token operator">-</span>base<span class="token punctuation">)</span> <span class="token operator">-</span>> <span class="token function">var</span><span class="token punctuation">(</span><span class="token operator">-</span><span class="token operator">-</span>dt<span class="token operator">-</span>typography<span class="token operator">-</span>body<span class="token operator">-</span>md<span class="token punctuation">)</span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">Update any references of <code v-pre>var(--dt-typography...)</code> with the updated equivalents. Refer to the mapping table below for clarity.</p>
<h3 id="using-css-utilities" tabindex="-1" class="d-docsite--header-3">Using CSS Utilities <a class="header-anchor d-link d-docsite--link d-link" href="#using-css-utilities" aria-hidden="true">#</a></h3>
<div class="language-less" data-highlighter="prismjs" data-ext="less"><pre v-pre><code class="language-less"><span class="line">class=<span class="token string">"d-label-plain-small"</span> <span class="token operator">-</span>> class=<span class="token string">"d-label--sm-plain"</span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">Replace any instances of the old naming convention with the corresponding new names. Refer to the mapping table below for clarity.</p>
<div class="d-bb d-bc-default">
  <table class="d-table">
    <thead>
      <tr>
        <th>Previous 👎</th>
        <th>New 👍</th>
      </tr>
    </thead>
    <tbody class="d-va-top">
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-headline-eyebrow</code></td>
        <td><code class="d-code--sm d-docsite-code">d-headline--eyebrow</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-headline-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-headline--sm</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-headline-soft-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-headline--sm-soft</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-headline-compact-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-headline--sm-compact</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-headline-compact-soft-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-headline--sm-soft-compact</code>
        </td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-headline-medium</code></td>
        <td><code class="d-code--sm d-docsite-code">d-headline--md</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-headline-compact-medium</code></td>
        <td><code class="d-code--sm d-docsite-code">d-headline--md-compact</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-headline-large</code></td>
        <td><code class="d-code--sm d-docsite-code">d-headline--lg</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-headline-compact-large</code></td>
        <td><code class="d-code--sm d-docsite-code">d-headline--lg-compact</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-headline-extra-large</code></td>
        <td><code class="d-code--sm d-docsite-code">d-headline--xl</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-headline-extra-extra-large</code></td>
        <td><code class="d-code--sm d-docsite-code">d-headline--xxl</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-body-base</code></td>
        <td><code class="d-code--sm d-docsite-code">d-body--md</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-body-compact</code></td>
        <td><code class="d-code--sm d-docsite-code">d-body--md-compact</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-body-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-body--sm</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-body-compact-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-body--sm-compact</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-label-base</code></td>
        <td><code class="d-code--sm d-docsite-code">d-label--md</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-label-compact</code></td>
        <td><code class="d-code--sm d-docsite-code">d-label--md-compact</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-label-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-label--sm</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-label-plain</code></td>
        <td><code class="d-code--sm d-docsite-code">d-label--md-plain</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-label-compact-plain</code></td>
        <td><code class="d-code--sm d-docsite-code">d-label--md-plain-compact</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-label-compact-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-label--sm-compact</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-label-plain-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-label--sm-plain</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-label-compact-plain-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-label--sm-plain-compact</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-helper-base</code></td>
        <td><code class="d-code--sm d-docsite-code">d-helper--md</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-helper-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-helper--sm</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-code-base</code></td>
        <td><code class="d-code--sm d-docsite-code">d-code--md</code></td>
      </tr>
      <tr>
        <td class="d-bgc-secondary"><code class="d-code--sm">d-code-small</code></td>
        <td><code class="d-code--sm d-docsite-code">d-code--sm</code></td>
      </tr>
    </tbody>
  </table>
</div>
<h3 id="when-inspecting-a-figma-file" tabindex="-1" class="d-docsite--header-3">When inspecting a Figma File <a class="header-anchor d-link d-docsite--link d-link" href="#when-inspecting-a-figma-file" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><img src="/assets/images/inspecting_figma.png" alt="Updates on Typography Tokens" class="d-docsite--image d-wmx100p"></p>
<p class="d-docsite--paragraph">When working with <a href="https://help.figma.com/hc/en-us/articles/15023121296151-Figma-for-VS-Code" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Figma for VS Code</a> or using <a href="https://help.figma.com/hc/en-us/articles/15023124644247-Guide-to-Dev-Mode" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Dev Mode</a> within the Figma File, you'll notice the new naming convention displayed with &quot;/&quot; in the elements. Simply replace &quot;/&quot; with &quot;-&quot; when referencing them.</p>
<p class="d-docsite--paragraph">e.g. In Figma <code v-pre>typography/label/md</code> translates to</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">CSS Var <code v-pre>var(--dt-typography-label-md)</code></li>
<li class="d-docsite--list-element">CSS Utility <code v-pre>d-label--md</code></li>
</ul>
<h3 id="for-designers" tabindex="-1" class="d-docsite--header-3">For Designers <a class="header-anchor d-link d-docsite--link d-link" href="#for-designers" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">You might notice prompts to update the library in your files. Fear not – this update won't alter any styles. We've even added searchable descriptions to each style to improve findability. For example, searching for <code v-pre>medium</code> will find text styles like <code v-pre>typography/body/md</code> and <code v-pre>typography/headline/md</code>. Plus, we've introduced compact versions for <code v-pre>xl</code> and <code v-pre>xxl</code> headlines to enhance versatility.</p>
<h4 class="d-docsite--header-4">Not Sure How It Affects Your Figma File?</h4>
<p class="d-docsite--paragraph">No worries! Create a branch in your file, update the library in &quot;All Pages,&quot; and take a closer look at the changes. Rest assured, this update primarily focuses on naming conventions and won't bring significant visual alterations.</p>
<video width="695" controls>
  <source src="/assets/images/branching_figma.mp4" type="video/mp4" alt="Branches in Figma">
  Your browser does not support the video tag.
</video>
<h2 id="got-questions" tabindex="-1" class="d-docsite--header-2">Got questions? <a class="header-anchor d-link d-docsite--link d-link" href="#got-questions" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">For more detailed information, visit our <RouteLink to="/design/typography/#styles" class="d-docsite--link d-link">Dialtone Typography docs</RouteLink>.</p>
<p class="d-docsite--paragraph">We're here to help! Reach out us in the <code v-pre>#dialtone</code> channel for any assistance you need.</p>
<p class="d-docsite--paragraph">Thanks for your patience and understanding,
Dialtone Team 💜</p>
</BlogPost>
</div></template>


<script setup>
import BlogPost from '@baseComponents/BlogPost.vue';
import { parse } from 'date-fns';
</script>